import { useEffect, useId, useMemo, useState } from "react";
import { Link, Outlet, useNavigate, useRouterState, getRouteApi } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { unwrap } from "@tern/api-client";
import { Inbox, CircleUserRound, ChevronDown, ChevronRight, Search, SquarePen, Plus, Settings, Bell, HelpCircle, Home, FileText, CircleDot, ListChecks, Flag } from "lucide-react";
import { CommandPalette } from "./CommandPalette";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import { ProjectCreateDialog } from "./ProjectCreateDialog";
import { ProjectIcon, type ProjectAppearance } from "./ProjectIcon";
import { IssueCreateDialog } from "./IssueCreateDialog";
import { Toaster } from "./Toaster";
import { api } from "@/lib/api";
import { useRealtime } from "@/lib/realtime";
import { useShortcuts } from "@/lib/shortcuts";
import { useProjectStore } from "@/lib/store";
import { useViews } from "@/lib/views";
import { unreadCountQuery } from "@/lib/inbox";

const route = getRouteApi("/$ws");

export const projectsQuery = (slug: string) => ({
  queryKey: ["projects", slug],
  queryFn: async () => unwrap(await api.GET("/api/workspaces/{slug}/projects", { params: { path: { slug } } })),
});

export function WorkspaceLayout() {
  const { workspace } = route.useRouteContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [newIssueOpen, setNewIssueOpen] = useState(false);
  const path = useRouterState({ select: (s) => s.location.pathname });
  const projects = useQuery(projectsQuery(workspace.slug));
  useRealtime(workspace.id);
  const hydrateViews = useViews((s) => s.hydrate);
  useEffect(() => void hydrateViews(), [hydrateViews]);

  // "C" anywhere: new issue in the current project (or the first one).
  const currentKey = path.match(/\/project\/([A-Z0-9]+)/)?.[1] ?? path.match(/\/issue\/([A-Z0-9]+)-/)?.[1];
  const targetKey = currentKey ?? projects.data?.[0]?.key;
  const targetData = useProjectStore((s) => (targetKey ? s.byId[s.idByKey[`${workspace.slug}/${targetKey}`] ?? ""] : undefined));
  const load = useProjectStore((s) => s.load);
  const openNewIssue = () => {
    if (!targetKey) return setNewProjectOpen(true);
    if (!targetData) void load(workspace.slug, targetKey).then(() => setNewIssueOpen(true));
    else setNewIssueOpen(true);
  };

  const onIssuesList = /\/issues\//.test(path);
  const hasTarget = !!targetData;
  const slug = workspace.slug;
  const shortcuts = useMemo(
    () => [
      { keys: "g i", run: () => void navigate({ to: "/$ws/inbox", params: { ws: slug } }) },
      { keys: "g m", run: () => void navigate({ to: "/$ws/my-issues", params: { ws: slug } }) },
      { keys: "g s", run: () => void navigate({ to: "/$ws/settings", params: { ws: slug } }) },
      { keys: "/", run: () => setPaletteOpen(true) },
      ...(onIssuesList ? [] : [{ keys: "c", run: openNewIssue }]),
    ],
    // openNewIssue reads the latest store/targetKey; re-create when those change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, slug, onIssuesList, targetKey, hasTarget],
  );
  useShortcuts(shortcuts);

  const base = `/${workspace.slug}`;
  const isActive = (p: string) => path === p || path.startsWith(p + "/");
  const openSearch = () => setPaletteOpen(true);
  const openNewProject = () => setNewProjectOpen(true);

  const navigation = (
    <>
        <div className="flex h-14 shrink-0 items-center gap-1 px-2.5">
          <WorkspaceSwitcher />
          <button className="btn btn-ghost h-8 w-7 shrink-0 justify-center p-0 text-muted" title={`${t("common.search")} (/)`} aria-label={t("common.search")} onClick={openSearch}>
            <Search className="h-4 w-4" />
          </button>
          <button className="btn h-8 w-8 shrink-0 justify-center rounded-lg border-border bg-elevated p-0 text-fg" title={`${t("nav.newIssue")} (C)`} aria-label={t("nav.newIssue")} onClick={openNewIssue}>
            <SquarePen className="h-4 w-4" />
          </button>
        </div>

        <nav aria-label={t("nav.navigation")} className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-3 pb-4 pt-3">
          <SideLink to={`${base}/inbox`} active={isActive(`${base}/inbox`)} icon={<Inbox className="h-4 w-4" />} badge={<UnreadBadge slug={workspace.slug} />}>{t("nav.inbox")}</SideLink>
          <SideLink to={`${base}/my-issues/assigned`} active={isActive(`${base}/my-issues`)} icon={<CircleUserRound className="h-4 w-4" />}>{t("nav.myIssues")}</SideLink>

          <SectionHeader label={t("nav.myProjects")} action={<Plus className="h-3.5 w-3.5" />} actionTitle={t("nav.newProject")} onAction={openNewProject}>
            {projects.data?.length === 0 && (
              <button className="my-1 rounded-lg border border-dashed border-border-strong px-3 py-3 text-left text-[12px] leading-relaxed text-muted transition-colors hover:bg-hover hover:text-fg" onClick={openNewProject}>
                {t("project.createFirst")}
              </button>
            )}
            {projects.data?.map((p) => (
              <ProjectTree key={p.id} slug={workspace.slug} project={p} path={path} />
            ))}
          </SectionHeader>
        </nav>

        <div className="flex shrink-0 items-center justify-between gap-2 px-3 py-3">
          <Link to="/$ws/settings" params={{ ws: workspace.slug }} aria-current={isActive(`${base}/settings`) ? "page" : undefined} className={`btn btn-ghost h-9 flex-1 justify-start gap-2.5 px-2.5 text-[13px] ${isActive(`${base}/settings`) ? "bg-active text-fg" : "text-muted"}`}>
            <Settings className="h-4 w-4" />
            <span>{t("nav.settings")}</span>
          </Link>
          <button className="btn btn-ghost h-8 w-8 justify-center rounded-full p-0 text-muted" title={`${t("nav.help")} (?)`} aria-label={t("nav.help")} onClick={openSearch}>
            <HelpCircle className="h-4 w-4" />
          </button>
        </div>
    </>
  );

  return (
    <div className="flex h-full min-h-0 overflow-hidden bg-sidebar">
      <aside className="flex w-[248px] shrink-0 flex-col bg-sidebar py-2">
        {navigation}
      </aside>
      <main className="my-2 mr-2 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-border bg-bg shadow-sm">
        <Outlet />
      </main>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} projects={projects.data ?? []} onNewIssue={openNewIssue} onNewProject={openNewProject} />
      <ProjectCreateDialog slug={workspace.slug} open={newProjectOpen} onOpenChange={setNewProjectOpen} />
      {targetData && <IssueCreateDialog d={targetData} open={newIssueOpen} onOpenChange={setNewIssueOpen} />}
      <Toaster />
    </div>
  );
}

function ProjectTree({ slug, project, path }: { slug: string; project: ProjectAppearance & { key: string; name: string }; path: string }) {
  const { t } = useTranslation();
  const itemsId = useId();
  const base = `/${slug}/project/${project.key}`;
  const issueHere = path.startsWith(`/${slug}/issue/${project.key}-`);
  const here = path === base || path.startsWith(base + "/") || issueHere;
  const [open, setOpen] = useState<boolean | null>(null);
  const expanded = open ?? here;
  const items = [
    { seg: "", label: t("nav.home"), icon: <Home className="h-3.5 w-3.5" /> },
    { seg: "/docs", label: t("nav.docs"), icon: <FileText className="h-3.5 w-3.5" /> },
    { seg: "/tasks", label: t("nav.tasks"), icon: <ListChecks className="h-3.5 w-3.5" /> },
    { seg: "/issues/all", label: t("nav.issues"), icon: <CircleDot className="h-3.5 w-3.5" />, match: "/issues" },
    { seg: "/milestones", label: t("nav.milestone"), icon: <Flag className="h-3.5 w-3.5" /> },
  ];
  return (
    <div>
      <button aria-expanded={expanded} aria-controls={itemsId} className={`flex h-9 w-full items-center gap-2.5 rounded-md px-2.5 text-[13px] transition-colors hover:bg-hover ${here ? "font-medium text-fg" : "text-muted hover:text-fg"}`} onClick={() => setOpen(!expanded)}>
        <ProjectIcon project={project} />
        <span className="min-w-0 flex-1 truncate text-left" title={project.name}>{project.name}</span>
        {expanded ? <ChevronDown className="h-3 w-3 text-faint" /> : <ChevronRight className="h-3 w-3 text-faint" />}
      </button>
      {expanded && (
        <div id={itemsId} className="mb-1 ml-4 flex flex-col gap-0.5">
          {items.map((it) => {
            const to = base + it.seg;
            const active = it.seg === "" ? path === base : path.startsWith(base + (it.match ?? it.seg)) || (it.match === "/issues" && issueHere);
            return (
              <SideLink key={it.seg} to={to} active={active} icon={it.icon} small>
                {it.label}
              </SideLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SideLink({ to, active, icon, children, small, badge }: { to: string; active: boolean; icon: React.ReactNode; children: React.ReactNode; small?: boolean; badge?: React.ReactNode }) {
  return (
    <Link to={to} aria-current={active ? "page" : undefined} className={`group flex items-center gap-2.5 rounded-md px-2.5 ${small ? "h-[34px] text-[13px]" : "h-9 text-[14px]"} transition-colors ${active ? "bg-active font-medium text-fg" : "text-muted hover:bg-hover hover:text-fg"}`}>
      <span className={`shrink-0 ${active ? "text-fg" : "text-muted group-hover:text-fg"}`}>{icon}</span>
      <span className="flex-1 truncate">{children}</span>
      {badge}
    </Link>
  );
}

/** Unread count for the sidebar + browser tab title. */
function UnreadBadge({ slug }: { slug: string }) {
  const q = useQuery(unreadCountQuery(slug));
  const count = q.data?.count ?? 0;
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\+?\)\s*/, "");
    document.title = count > 0 ? `(${count > 99 ? "99+" : count}) ${base}` : base;
  }, [count]);
  if (count === 0) return null;
  return <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-md bg-active px-1.5 text-[11px] font-medium text-fg tabular-nums">{count > 99 ? "99+" : count}</span>;
}

function InboxBell() {
  const { workspace } = route.useRouteContext();
  const { t } = useTranslation();
  const q = useQuery(unreadCountQuery(workspace.slug));
  const count = q.data?.count ?? 0;
  return (
    <Link to="/$ws/inbox" params={{ ws: workspace.slug }} className="btn btn-ghost relative h-8 w-8 shrink-0 justify-center p-0 text-muted hover:text-fg" title={t("nav.inbox")} aria-label={t("nav.inbox")}>
      <Bell className="h-4 w-4" />
      {count > 0 && <span className="absolute right-0.5 top-0.5 h-2 w-2 rounded-full bg-accent" />}
    </Link>
  );
}

function SectionHeader({ label, action, actionTitle, onAction, children }: { label: string; action?: React.ReactNode; actionTitle?: string; onAction?: () => void; children: React.ReactNode }) {
  const [open, setOpen] = useState(true);
  const contentId = useId();
  return (
    <div className="mt-7">
      <div className="flex h-8 items-center justify-between gap-2 pl-2.5 text-[12px] font-medium text-faint">
        <button aria-expanded={open} aria-controls={contentId} className="flex h-full min-w-0 flex-1 items-center gap-1.5 text-left hover:text-fg" onClick={() => setOpen((o) => !o)}>
          <span>{label}</span>
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>
        {action && (
          <button className="flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-hover hover:text-fg" title={actionTitle} aria-label={actionTitle} onClick={onAction}>
            {action}
          </button>
        )}
      </div>
      {open && <div id={contentId} className="mt-1 flex flex-col gap-1.5">{children}</div>}
    </div>
  );
}

/** Page chrome shared by every workspace page: breadcrumb + right-side tools. */
export function PageHeader({ crumbs, besideCrumbs, right, embedded }: { crumbs: React.ReactNode[]; besideCrumbs?: React.ReactNode; right?: React.ReactNode; embedded?: boolean }) {
  return (
    <header className={`flex shrink-0 items-center justify-between gap-3 border-b border-border ${embedded ? "h-[44px] bg-sidebar/40 px-4" : "h-14 px-6"}`}>
      <div className={`flex min-w-0 flex-1 items-center gap-2 ${embedded ? "text-[13px]" : "text-[14px]"}`}>
        {crumbs.map((c, i) => (
          <span key={i} className={`flex min-w-0 items-center gap-2 ${i < crumbs.length - 1 ? "shrink-[2]" : ""}`}>
            {i > 0 && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-faint" />}
            <span className={`truncate ${i === crumbs.length - 1 ? "font-medium text-fg" : "text-muted"}`}>{c}</span>
          </span>
        ))}
        {besideCrumbs}
      </div>
      <div className="ml-auto flex max-w-full shrink-0 items-center gap-1.5 text-muted">
        {right}
        {!embedded && <InboxBell />}
      </div>
    </header>
  );
}
